import { useRef, useState, useEffect, useCallback } from "react";
import { motion, useInView, AnimatePresence } from "framer-motion";
import { services } from "@/data/caseStudies";
import { ArrowUpRight } from "lucide-react";
import { useTheme } from "@/components/ThemeProvider";
import SliderNav from "@/components/SliderNav";
import ContactSheet from "@/components/ContactSheet";
import framerCommerceLogo from "@/assets/tools/framer-commerce.png";
import DallasLocalsCTA from "@/components/DallasLocalsCTA";

/** Inverted-dot custom cursor link for Framer Commerce logo */
const FramerCommerceLink = () => {
  const linkRef = useRef<HTMLAnchorElement>(null);
  const dotRef = useRef<HTMLDivElement>(null);
  const [hovering, setHovering] = useState(false);

  const onMove = useCallback((e: React.MouseEvent) => {
    if (!linkRef.current || !dotRef.current) return;
    const rect = linkRef.current.getBoundingClientRect();
    dotRef.current.style.left = `${e.clientX - rect.left}px`;
    dotRef.current.style.top = `${e.clientY - rect.top}px`;
  }, []);

  return (
    <a
      ref={linkRef}
      href="https://framercommerce.com"
      target="_blank"
      rel="noopener noreferrer"
      aria-label="Visit Framer Commerce"
      className="relative inline-flex items-center"
      style={{ cursor: "none" }}
      onClick={(e) => e.stopPropagation()}
      onMouseEnter={() => setHovering(true)}
      onMouseLeave={() => setHovering(false)}
      onMouseMove={onMove}
    >
      <div
        ref={dotRef}
        className="pointer-events-none absolute rounded-full"
        style={{
          width: 20,
          height: 20,
          backgroundColor: "white",
          mixBlendMode: "difference",
          opacity: hovering ? 1 : 0,
          transform: `translate(-50%, -50%) scale(${hovering ? 1 : 0.3})`,
          transition: "opacity 0.15s ease-out, transform 0.15s ease-out",
        }}
      />
      <img
        src={framerCommerceLogo}
        alt="Framer Commerce"
        className="h-3.5 dark:invert"
      />
    </a>
  );
};

const ServicesSection = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-80px" });
  const [activeIndex, setActiveIndex] = useState(0);
  const { mode } = useTheme();

  // Preload all service SVGs on mount
  useEffect(() => {
    services.forEach((s) => {
      const img = new Image();
      img.src = s.image;
      if (s.imageDark) {
        const imgDark = new Image();
        imgDark.src = s.imageDark;
      }
    });
  }, []);

  const current = services[activeIndex];

  const goTo = (i: number) => {
    if (i >= 0 && i < services.length) setActiveIndex(i);
  };

  return (
    <section id="services" ref={ref} className="section-light py-16 md:py-24 px-5 md:px-10 lg:px-14">
      <div>
      {/* Two-column layout */}
      <div className="flex flex-col md:flex-row md:items-stretch md:gap-0 min-h-[70vh]">
        {/* Left: Content */}
        <div className="flex-1 flex flex-col justify-between pr-0 md:pr-12 lg:pr-20">
          <motion.div
            initial={{ opacity: 0 }}
            animate={isInView ? { opacity: 1 } : {}}
            transition={{ duration: 0.6 }}
          >
            <p className="font-body text-[9px] font-medium tracking-[0.25em] uppercase text-muted-foreground mb-2">
              03 — Our Services
            </p>
          </motion.div>

          <AnimatePresence mode="wait">
            <motion.div
              key={activeIndex}
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              transition={{ duration: 0.45, ease: [0.25, 0.46, 0.45, 0.94] }}
              className="flex-1 flex flex-col justify-center"
            >
              <h2 className="font-display italic text-4xl md:text-[48px] lg:text-[56px] leading-[1.1] text-foreground mb-8 max-w-md">
                {current.title}
              </h2>

              <p className="font-body text-[13px] md:text-[14px] text-muted-foreground leading-[1.8] max-w-lg mb-8">
                {current.description}
              </p>

              {current.details && current.details.length > 0 && (
                <div className="grid grid-cols-2 gap-x-10 gap-y-2.5 mb-10 max-w-lg">
                  {current.details.map((detail) => (
                    <span
                      key={detail}
                      className="font-body text-[12px] text-foreground/70 flex items-center gap-2"
                    >
                      <span className="w-1 h-1 rounded-full bg-foreground/40 flex-shrink-0" />
                      {detail === "Synced inventory management" && (
                        <FramerCommerceLink />
                      )}
                      {detail}
                    </span>
                  ))}
                </div>
              )}

              <div className="flex items-center gap-4">
                <ContactSheet defaultProjectType={current.title}>
                  <button className="inline-flex items-center gap-1.5 px-5 py-2.5 border border-foreground text-foreground font-body text-[9px] font-medium tracking-[0.15em] uppercase hover:bg-foreground hover:text-background transition-colors">
                    Let's talk
                    <ArrowUpRight size={10} />
                  </button>
                </ContactSheet>
                <span className="font-body text-[12px] text-muted-foreground">
                  {current.pricing}
                </span>
              </div>
            </motion.div>
          </AnimatePresence>

          {/* Navigation */}
          <div className="mt-10 md:mt-14">
            <SliderNav
              activeIndex={activeIndex}
              total={services.length}
              onNavigate={goTo}
              label="service"
            />
          </div>
        </div>

        {/* Right: All images stacked, only active visible */}
        <div className="flex-1 flex items-center justify-center mt-10 md:mt-0">
          <div className="w-full max-w-[520px] lg:max-w-[640px] xl:max-w-[720px] relative aspect-[4/3]">
            {services.map((s, i) => {
              const src = mode === "dark" && s.imageDark ? s.imageDark : s.image;
              const isActive = i === activeIndex;
              return (
                <motion.div
                  key={i}
                  initial={false}
                  animate={{
                    opacity: isActive ? 1 : 0,
                    scale: isActive ? 1 : 0.96,
                  }}
                  transition={{ duration: 0.45, ease: [0.25, 0.46, 0.45, 0.94] }}
                  className="absolute inset-0"
                  style={{ pointerEvents: isActive ? "auto" : "none" }}
                >
                  <img
                    src={src}
                    alt={s.title}
                    className="w-full h-full object-contain"
                  />
                </motion.div>
              );
            })}
          </div>
        </div>
      </div>
      </div>

      {/* Dallas Locals CTA nested below carousel */}
      <div id="dallas-locals" className="mt-16 md:mt-24 pt-12 md:pt-16">
        <DallasLocalsCTA />
      </div>
    </section>
  );
};

export default ServicesSection;
